import React from 'react';
import { createPortal } from 'react-dom';
import { Users, X, Phone, Bell, Search, UserCircle2, Trash2, Plus, Shield, Crown } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { CommanServices, normalizeIncidentRecipientDirectory } from '@/services/commanServices';
import { DutyBadge } from './PersonnelOfferPrompt';
import { loadAssignUsers, loadAssignDuty } from './AssignPersonnelModal';

// Crew staff modal — mirrors the department "View Staff" modal: a Staff &
// Contact tab for everyone, and (for admins) a Manage Members tab.
//
// Crew membership comes from two places:
//  - users whose profile has crew_id = this crew ("profile" members — managed
//    from App Users / Teams & Crews, shown here read-only), and
//  - the crew's escalation rules ("rule" members — fully manageable here via
//    PUT /teams/{team_id}/crews/{crew_id}).
const CrewStaffModal = ({ crew, incidentId = null, isAdmin = false, onClose, onCall, onNotify }) => {
  const [tab, setTab] = React.useState('contact'); // contact | manage
  const [users, setUsers] = React.useState([]);
  const [duty, setDuty] = React.useState({});
  const [teamDoc, setTeamDoc] = React.useState(null);
  const [query, setQuery] = React.useState('');
  const [busy, setBusy] = React.useState(false);

  const reload = React.useCallback(async () => {
    if (!isAdmin && incidentId) {
      const response = await CommanServices.getIncidentRecipientDirectory(incidentId);
      const directory = normalizeIncidentRecipientDirectory(response);
      setUsers(directory.users);
      setDuty(directory.duty);
      setTeamDoc(directory.teams.find((team) => String(team.id || team._id) === String(crew.team_id)) || null);
      return;
    }
    const [{ raw }, dutyMap, teamsRes] = await Promise.all([
      loadAssignUsers(),
      loadAssignDuty(),
      CommanServices.getTeamsData().catch(() => ({ data: [] })),
    ]);
    setUsers(raw);
    setDuty(dutyMap || {});
    const teams = Array.isArray(teamsRes?.data) ? teamsRes.data : [];
    setTeamDoc(teams.find((t) => String(t.id) === String(crew.team_id)) || null);
  }, [crew.team_id, incidentId, isAdmin]);

  React.useEffect(() => { reload(); }, [reload]);

  const crewDoc = React.useMemo(
    () => (teamDoc?.crews || []).find((c) => String(c.id) === String(crew.id)) || null,
    [teamDoc, crew.id]
  );

  const resolveUser = React.useCallback((ref) => {
    const r = String(ref || '').toLowerCase();
    return users.find((u) =>
      String(u.id).toLowerCase() === r ||
      String(u.email || '').toLowerCase() === r ||
      String(u.user_account || '').toLowerCase() === r
    ) || null;
  }, [users]);

  // Merge profile members + escalation-rule members (deduped, tagged by source).
  const members = React.useMemo(() => {
    const map = new Map();
    users.forEach((u) => {
      if (u.is_active === false) return;
      if (String(u.crew_id || '') === String(crew.id)) {
        map.set(String(u.id), { user: u, sources: new Set(['profile']) });
      }
    });
    (crewDoc?.escalation_rules || []).forEach((rule) => {
      if (rule?.is_active === false) return;
      const targets = Array.isArray(rule.escalated_to) ? rule.escalated_to : [rule.escalated_to];
      targets.filter(Boolean).forEach((t) => {
        const u = resolveUser(t);
        if (!u) return;
        const key = String(u.id);
        if (!map.has(key)) map.set(key, { user: u, sources: new Set() });
        map.get(key).sources.add('rule');
      });
    });
    return [...map.values()].map((m) => ({
      userId: String(m.user.id),
      name: m.user.name || m.user.full_name || m.user.email || 'User',
      email: m.user.email || '',
      viaProfile: m.sources.has('profile'),
      viaRule: m.sources.has('rule'),
      isLeader: (crewDoc?.leaders || []).map(String).includes(String(m.user.id)),
    })).sort((a, b) => a.name.localeCompare(b.name));
  }, [users, crewDoc, crew.id, resolveUser]);

  const memberIds = React.useMemo(() => new Set(members.map((m) => m.userId)), [members]);

  // Serialize current rules into the CrewUpdate shape (the PUT replaces the list).
  const rulesPayload = React.useCallback(() => (crewDoc?.escalation_rules || []).map((r) => ({
    escalated_to: r.escalated_to,
    escalation_trigger: r.escalation_trigger || 'Verification',
    response_time: r.response_time || 'Immediate',
    response_time_custom: r.response_time_custom ?? null,
    order_index: r.order_index ?? 0,
    auto_trigger_action_card_id: r.auto_trigger_action_card_id ?? null,
    is_active: r.is_active !== false,
  })), [crewDoc]);

  const addMember = async (u) => {
    if (!teamDoc || !crewDoc) return;
    setBusy(true);
    try {
      const rules = rulesPayload();
      rules.push({
        escalated_to: String(u.id),
        escalation_trigger: 'Verification',
        response_time: 'Immediate',
        response_time_custom: null,
        order_index: rules.length,
        auto_trigger_action_card_id: null,
        is_active: true,
      });
      const res = await CommanServices.updateTeamCrew(teamDoc.id, crewDoc.id, { escalation_rules: rules });
      if (res?.status === false) throw new Error(res?.message || 'Failed');
      toast.success(`${u.name || u.email} added to ${crew.crew_name || crew.name}`);
      await reload();
    } catch (e) {
      toast.error(e?.message || 'Failed to add member');
    } finally { setBusy(false); }
  };

  const removeMember = async (m) => {
    if (!teamDoc || !crewDoc) return;
    setBusy(true);
    try {
      const target = users.find((u) => String(u.id) === m.userId);
      const keys = new Set([m.userId.toLowerCase(), String(target?.email || '').toLowerCase(), String(target?.user_account || '').toLowerCase()].filter(Boolean));
      const rules = rulesPayload().filter((r) => {
        const targets = Array.isArray(r.escalated_to) ? r.escalated_to : [r.escalated_to];
        return !targets.some((t) => keys.has(String(t || '').toLowerCase()));
      }).map((r, i) => ({ ...r, order_index: i }));
      const leaders = (crewDoc.leaders || []).map(String).filter((id) => id !== m.userId);
      const res = await CommanServices.updateTeamCrew(teamDoc.id, crewDoc.id, { escalation_rules: rules, leaders });
      if (res?.status === false) throw new Error(res?.message || 'Failed');
      toast.success(`${m.name} removed from ${crew.crew_name || crew.name}`);
      await reload();
    } catch (e) {
      toast.error(e?.message || 'Failed to remove member');
    } finally { setBusy(false); }
  };

  const toggleLeader = async (m) => {
    if (!teamDoc || !crewDoc) return;
    setBusy(true);
    try {
      const leaders = new Set((crewDoc.leaders || []).map(String));
      if (leaders.has(m.userId)) leaders.delete(m.userId); else leaders.add(m.userId);
      const res = await CommanServices.updateTeamCrew(teamDoc.id, crewDoc.id, { leaders: [...leaders] });
      if (res?.status === false) throw new Error(res?.message || 'Failed');
      toast.success(`${m.name} ${leaders.has(m.userId) ? 'is now' : 'is no longer'} a crew leader`);
      await reload();
    } catch (e) {
      toast.error(e?.message || 'Failed to update crew leader');
    } finally { setBusy(false); }
  };

  const q = query.trim().toLowerCase();
  const addable = users.filter((u) =>
    u.is_active !== false && !memberIds.has(String(u.id)) &&
    (!q || ((u.name || '') + ' ' + (u.email || '')).toLowerCase().includes(q))
  ).slice(0, 30);

  const crewLabel = crew.crew_name || crew.name || 'Crew';

  return createPortal(
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 backdrop-blur-sm" data-testid="crew-staff-modal">
      <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[88vh] flex flex-col m-4 overflow-hidden">
        {/* Header — same chrome as the department staff modal */}
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between bg-gradient-to-r from-slate-800 to-indigo-900 rounded-t-2xl">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-white/10 flex items-center justify-center">
              <Shield className="h-5 w-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-white">{crewLabel}</h2>
              <p className="text-xs text-indigo-300">Crew Staff{crew.team_name ? ` · ${crew.team_name}` : ''}</p>
            </div>
          </div>
          <Button variant="ghost" size="sm" className="text-white/70 hover:text-white hover:bg-white/10 rounded-full" onClick={onClose} data-testid="crew-staff-close">
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-100 px-6 bg-white flex-shrink-0">
          <button
            onClick={() => setTab('contact')}
            className={`px-4 py-2.5 text-sm font-semibold border-b-2 transition-colors ${tab === 'contact' ? 'border-indigo-600 text-indigo-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
          >
            Staff &amp; Contact
          </button>
          {isAdmin && (
            <button
              onClick={() => setTab('manage')}
              data-testid="crew-staff-manage-tab"
              className={`px-4 py-2.5 text-sm font-semibold border-b-2 transition-colors ${tab === 'manage' ? 'border-indigo-600 text-indigo-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
            >
              Manage Members
            </button>
          )}
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {tab === 'contact' && (
            <div className="space-y-2.5" data-testid="crew-staff-list">
              {members.map((m) => (
                <div key={m.userId} className="flex items-center gap-3 p-3 bg-white border border-gray-100 rounded-xl shadow-sm">
                  <div className="flex items-center justify-center w-9 h-9 rounded-xl bg-gradient-to-br from-indigo-500 to-violet-600 text-white font-bold text-sm flex-shrink-0">
                    {m.name.charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-slate-800 truncate">{m.name}</p>
                    {m.email && <p className="text-[11px] text-slate-500 truncate">{m.email}</p>}
                  </div>
                  <DutyBadge status={duty[m.userId]?.status} />
                  {m.isLeader && (
                    <span className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2 py-1 text-[10px] font-bold uppercase text-amber-700">
                      <Crown className="h-3 w-3" /> Leader
                    </span>
                  )}
                  {onCall && (
                    <Button variant="outline" size="sm" className="h-7 text-xs px-2 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border-emerald-200"
                      onClick={() => onCall(m)}>
                      <Phone className="h-3 w-3 mr-1" /> Call
                    </Button>
                  )}
                  {onNotify && (
                    <Button variant="outline" size="sm" className="h-7 text-xs px-2 bg-amber-50 hover:bg-amber-100 text-amber-700 border-amber-200"
                      onClick={() => onNotify(m)}>
                      <Bell className="h-3 w-3 mr-1" /> Notify
                    </Button>
                  )}
                </div>
              ))}
              {members.length === 0 && (
                <p className="text-sm text-gray-400 text-center py-6">No members in this crew yet{isAdmin ? ' — add some from the Manage Members tab.' : '.'}</p>
              )}
            </div>
          )}

          {tab === 'manage' && isAdmin && (
            <div className="space-y-5">
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">Current members</p>
                <div className="space-y-2" data-testid="crew-staff-manage-list">
                  {members.map((m) => (
                    <div key={m.userId} className="flex items-center gap-3 p-2.5 border border-gray-100 rounded-lg">
                      <UserCircle2 className="h-4 w-4 text-blue-500 flex-shrink-0" />
                      <span className="flex-1 text-sm text-gray-800 truncate">{m.name}<span className="ml-1.5 text-[11px] text-gray-400">{m.email}</span></span>
                      <button
                        type="button"
                        role="switch"
                        aria-checked={m.isLeader}
                        disabled={busy}
                        onClick={() => toggleLeader(m)}
                        data-testid={`crew-leader-toggle-${m.userId}`}
                        className={`inline-flex h-7 items-center gap-1.5 rounded-full border px-2.5 text-[10px] font-bold uppercase transition-colors ${m.isLeader ? 'border-amber-300 bg-amber-50 text-amber-700' : 'border-gray-200 bg-white text-gray-500 hover:border-amber-200'}`}
                        title="Leadership stays dormant until this leader or a member of this crew is involved in an incident."
                      >
                        <Crown className="h-3 w-3" /> {m.isLeader ? 'Leader' : 'Make leader'}
                      </button>
                      {m.viaRule ? (
                        <Button variant="ghost" size="sm" disabled={busy} className="h-7 px-2 text-red-500 hover:text-red-700 hover:bg-red-50"
                          data-testid={`crew-member-remove-${m.userId}`} onClick={() => removeMember(m)}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      ) : (
                        <span className="text-[10px] text-gray-400 font-medium" title="This user's profile points at this crew — manage it from App Users / Teams & Crews.">
                          via user profile
                        </span>
                      )}
                    </div>
                  ))}
                  {members.length === 0 && <p className="text-sm text-gray-400">No members yet.</p>}
                </div>
              </div>

              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">Add member</p>
                <div className="relative mb-2">
                  <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                  <input
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search users to add…"
                    data-testid="crew-staff-add-search"
                    className="w-full text-sm border border-gray-200 rounded-md pl-8 pr-2.5 py-2 outline-none focus:border-indigo-400"
                  />
                </div>
                <div className="max-h-52 overflow-y-auto border border-gray-100 rounded-lg divide-y divide-gray-50">
                  {addable.map((u) => (
                    <button key={u.id} type="button" disabled={busy}
                      data-testid={`crew-staff-add-${u.id}`}
                      onClick={() => addMember(u)}
                      className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm hover:bg-indigo-50 disabled:opacity-50">
                      <Plus className="h-3.5 w-3.5 text-indigo-500 flex-shrink-0" />
                      <span className="flex-1 truncate">{u.name || u.full_name || u.email}</span>
                      <DutyBadge status={duty[String(u.id)]?.status} />
                    </button>
                  ))}
                  {addable.length === 0 && <p className="px-3 py-3 text-xs text-gray-400">No matching users.</p>}
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="px-6 py-3 border-t border-gray-100 flex items-center justify-between">
          <span className="text-[11px] text-gray-400 inline-flex items-center gap-1"><Users className="h-3 w-3" /> {members.length} member{members.length === 1 ? '' : 's'}</span>
          <Button size="sm" onClick={onClose}>Done</Button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default CrewStaffModal;
